import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiX, FiTrash2 } from 'react-icons/fi';
import { FaTelegramPlane } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import { isAdminRole } from '../../utils/adminRoles';

const MAX_BOTS = 10;

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalToIso(localStr) {
  if (!localStr || !String(localStr).trim()) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function toTimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Нормализованный тип из API (старые записи без schedule_recurrence) */
function templateRecurrenceRaw(t) {
  const r = String(t.schedule_recurrence || '')
    .trim()
    .toLowerCase();
  if (r === 'weekly' || r === 'weekdays' || r === 'weekends' || r === 'once' || r === 'none') return r;
  if (t.scheduled_at) return 'once';
  return 'none';
}

function formatScheduleSummary(t) {
  const r = templateRecurrenceRaw(t);
  if (r === 'none') return null;
  if (r === 'once' && t.scheduled_at) {
    return `Один раз: ${new Date(t.scheduled_at).toLocaleString()}`;
  }
  const time = t.schedule_time || toTimeLocalValue(t.scheduled_at) || '—';
  const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  if (r === 'weekly') {
    const wd = t.schedule_weekday != null ? Number(t.schedule_weekday) : null;
    return `Раз в неделю: ${wd != null && !Number.isNaN(wd) ? days[wd] : '?'}, ${time}`;
  }
  if (r === 'weekdays') return `По будням (без выходных), ${time}`;
  if (r === 'weekends') return `Только выходные, ${time}`;
  if (t.scheduled_at) return `Следующая отправка: ${new Date(t.scheduled_at).toLocaleString()}`;
  return null;
}

function templateEnabled(t) {
  if (t == null) return true;
  return t.enabled !== false && t.enabled !== 0;
}

/** Статус сессии Telethon — всегда по-русски */
function telethonReasonRu(reason) {
  const s = String(reason || '').trim();
  if (!s) return s;
  const map = {
    'api_id / api_hash not set': 'Не заданы api_id / api_hash',
    'Session file not created yet': 'Файл сессии ещё не создан',
    'Session not authorized — send code and sign in': 'Вход не завершён — получите код и нажмите «Войти»',
    'Не заданы api_id / api_hash': 'Не заданы api_id / api_hash',
    'Файл сессии ещё не создан': 'Файл сессии ещё не создан',
    'Сессия не авторизована — отправьте код и войдите': 'Вход не завершён — получите код и нажмите «Войти»',
    NEED_PASSWORD: 'Нужен облачный пароль Telegram (см. шаг 3 ниже)',
  };
  return map[s] || s.replace(/[\uFFFD]/g, '');
}

const WEEKDAY_OPTIONS = [
  { v: 1, label: 'Понедельник' },
  { v: 2, label: 'Вторник' },
  { v: 3, label: 'Среда' },
  { v: 4, label: 'Четверг' },
  { v: 5, label: 'Пятница' },
  { v: 6, label: 'Суббота' },
  { v: 0, label: 'Воскресенье' },
];

function TabPill({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minWidth: 0,
        padding: '12px 10px',
        borderRadius: 12,
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 13,
        lineHeight: 1.25,
        textAlign: 'center',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        border: active ? '2px solid #0369a1' : '2px solid #38bdf8',
        background: active
          ? 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)'
          : 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
        color: active ? '#ffffff' : '#0c4a6e',
        boxShadow: active
          ? '0 6px 18px rgba(14, 165, 233, 0.5), 0 0 0 1px rgba(255,255,255,0.25) inset'
          : '0 2px 10px rgba(14, 165, 233, 0.25), 0 0 0 1px rgba(255,255,255,0.6) inset',
      }}
    >
      {children}
    </button>
  );
}

/** Удобный переключатель вкл/выкл шаблона */
function EnableSwitch({ on, disabled, onToggle, labelOn = 'Вкл', labelOff = 'Выкл' }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        background: 'transparent',
        padding: '4px 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: on ? '#0f766e' : '#64748b',
          minWidth: 36,
          textAlign: 'right',
        }}
      >
        {on ? labelOn : labelOff}
      </span>
      <span
        style={{
          position: 'relative',
          width: 50,
          height: 28,
          borderRadius: 999,
          background: on ? 'linear-gradient(90deg, #22c55e, #14b8a6)' : '#cbd5e1',
          boxShadow: on ? '0 2px 10px rgba(34, 197, 94, 0.45)' : 'inset 0 1px 3px rgba(0,0,0,0.12)',
          flexShrink: 0,
          transition: 'background 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: on ? 24 : 3,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            transition: 'left 0.2s ease',
          }}
        />
      </span>
    </button>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#0c4a6e',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #7dd3fc',
  fontSize: 15,
  background: '#fff',
  color: '#0f172a',
  boxShadow: '0 1px 2px rgba(14, 165, 233, 0.08)',
};

const cardShell = {
  background: '#fff',
  borderRadius: 16,
  border: '1px solid #bae6fd',
  boxShadow: '0 4px 20px rgba(14, 165, 233, 0.12), 0 0 0 1px rgba(255,255,255,0.8) inset',
  borderLeft: '4px solid #0ea5e9',
};

function ImagePicker({ onPick, disabled, isMobile }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f && onPick) onPick(f);
          e.target.value = '';
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f && onPick) onPick(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => fileRef.current?.click()}
        style={{
          padding: isMobile ? '10px 12px' : '8px 12px',
          borderRadius: 10,
          border: '1px solid #7dd3fc',
          background: '#fff',
          color: '#0369a1',
          fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        Выбрать из галереи
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => cameraRef.current?.click()}
        style={{
          padding: isMobile ? '10px 12px' : '8px 12px',
          borderRadius: 10,
          border: '1px solid #86efac',
          background: '#f0fdf4',
          color: '#166534',
          fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        Снять камерой
      </button>
    </div>
  );
}

function statusRu(s) {
  switch (s) {
    case 'pending':
      return 'Ожидает отправки';
    case 'sent':
      return 'Отправлено';
    case 'failed':
      return 'Ошибка';
    case 'sending':
      return 'Отправка…';
    default:
      return 'Без расписания';
  }
}

function WorkQueueTab({ templates, saving, onToggleEnabled, onDelete, onOpenTemplates, isMobile, swipeHandlersFor, deletingId }) {
  const isOn = (t) => templateEnabled(t);
  const inQueue = (t) =>
    isOn(t) &&
    t.scheduled_at &&
    (t.schedule_status === 'pending' || t.schedule_status === 'sending');
  const active = templates.filter(inQueue);
  const failed = templates.filter((t) => isOn(t) && t.schedule_status === 'failed');
  const off = templates.filter(
    (t) => !isOn(t) && (templateRecurrenceRaw(t) !== 'none' || Boolean(t.scheduled_at))
  );

  const section = (title, subtitle, list) => (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 17, color: '#0f172a' }}>{title}</h3>
      {subtitle ? (
        <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{subtitle}</p>
      ) : null}
      {list.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Нет записей</p>
      ) : (
        list.map((t) => (
          <QueueTemplateRow
            key={t.id}
            t={t}
            saving={saving}
            isDeleting={deletingId === t.id}
            isMobile={isMobile}
            swipeHandlers={swipeHandlersFor ? swipeHandlersFor(t.id) : undefined}
            onToggle={() => onToggleEnabled(t.id, !templateEnabled(t))}
            onDelete={() => onDelete(t.id)}
          />
        ))
      )}
    </div>
  );

  return (
    <div>
      <p style={{ margin: '0 0 8px', fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
        Контроль очереди и статусов. Редактирование текстов и расписания — во вкладке «Шаблоны бота».
      </p>
      <button
        type="button"
        onClick={onOpenTemplates}
        style={{
          marginBottom: 22,
          padding: '10px 16px',
          borderRadius: 12,
          border: '2px solid #38bdf8',
          background: 'linear-gradient(180deg, #ffffff, #e0f2fe)',
          color: '#0369a1',
          fontWeight: 700,
          fontSize: 14,
          cursor: 'pointer',
          boxShadow: '0 2px 12px rgba(14, 165, 233, 0.25)',
        }}
      >
        Перейти к шаблонам
      </button>
      {section('В очереди (по расписанию)', 'Включённые шаблоны, ожидающие отправки.', active)}
      {section('Ошибки отправки', 'Включённые шаблоны с неудачной последней попыткой.', failed)}
      {section(
        'Отключены (не исполняются)',
        'Расписание в базе сохранено, отправка отключена до включения.',
        off
      )}
    </div>
  );
}

function QueueTemplateRow({ t, saving, onToggle, onDelete, isMobile, swipeHandlers, isDeleting }) {
  return (
    <div
      {...(swipeHandlers || {})}
      style={{
        ...cardShell,
        padding: 14,
        marginBottom: 10,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        justifyContent: 'space-between',
        opacity: isDeleting ? 0.55 : 1,
        transition: 'opacity 0.15s ease',
        outline: isDeleting ? '2px dashed #f97316' : undefined,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{t.title || 'Без названия'}</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Бот: {t.bot_label || `#${t.bot_id}`}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          Chat ID (из сохранённых настроек бота):{' '}
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontWeight: 600,
              color: String(t.bot_chat_id || '').trim() ? '#0f172a' : '#dc2626',
            }}
          >
            {String(t.bot_chat_id || '').trim() || 'не задан — откройте «Настройки ботов» и сохраните'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>
          {statusRu(t.schedule_status)}
          {formatScheduleSummary(t) ? ` · ${formatScheduleSummary(t)}` : ''}
        </div>
        {t.scheduled_at ? (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            Слот времени: {new Date(t.scheduled_at).toLocaleString()}
          </div>
        ) : null}
        {t.sent_at ? (
          <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>
            Последняя отправка: {new Date(t.sent_at).toLocaleString()}
          </div>
        ) : null}
        {t.last_error ? (
          <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{t.last_error}</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <EnableSwitch
          on={templateEnabled(t)}
          disabled={saving}
          onToggle={onToggle}
          labelOn="Вкл"
          labelOff="Выкл"
        />
        {isMobile ? (
          <span style={{ fontSize: 11, color: '#94a3b8', maxWidth: 120, textAlign: 'right' }}>
            Свайп влево/вправо — удалить
          </span>
        ) : null}
        <button
          type="button"
          aria-label="Удалить шаблон"
          disabled={saving || isDeleting}
          onClick={onDelete}
          style={{
            border: 'none',
            background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
            color: '#b91c1c',
            padding: 10,
            borderRadius: 10,
            cursor: saving || isDeleting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
          }}
        >
          <FiTrash2 size={18} />
        </button>
      </div>
    </div>
  );
}

function normalizeTemplateAttachments(t) {
  const out = [];
  const push = (x) => {
    if (!x || typeof x !== 'object') return;
    const kind = String(x.kind || '').trim();
    if (kind !== 'image' && kind !== 'document') return;
    const url = String(x.url || '').trim();
    if (!url) return;
    out.push({
      kind,
      url,
      name: x.name ? String(x.name) : '',
      mime: x.mime ? String(x.mime) : '',
    });
  };
  try {
    const arr = JSON.parse(String(t?.attachments_json || '[]'));
    if (Array.isArray(arr)) arr.forEach(push);
  } catch {
    // ignore
  }
  if (!out.length && t?.image_url) {
    out.push({ kind: 'image', url: t.image_url, name: t.image_name || '', mime: 'image/jpeg' });
  }
  if (!out.length && t?.file_url) {
    out.push({ kind: 'document', url: t.file_url, name: t.file_name || '', mime: t.file_mime || '' });
  }
  return out;
}

function generateOfficeLinkSecret(len = 64) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_!@#$%^&*';
  const targetLen = Math.max(16, Math.min(256, Number(len) || 64));
  const arr = new Uint32Array(targetLen);
  try {
    if (window?.crypto?.getRandomValues) {
      window.crypto.getRandomValues(arr);
    } else {
      for (let i = 0; i < targetLen; i += 1) arr[i] = Math.floor(Math.random() * 0xffffffff);
    }
  } catch {
    for (let i = 0; i < targetLen; i += 1) arr[i] = Math.floor(Math.random() * 0xffffffff);
  }
  let out = '';
  for (let i = 0; i < targetLen; i += 1) {
    out += chars.charAt(arr[i] % chars.length);
  }
  return out;
}

export default function TelegramBotsModal({ open, onClose, isMobile, sidebarOffset = 320 }) {
  const { state } = useApp();
  const isAdminUser = isAdminRole(state.user?.role);
  const [tab, setTab] = useState('queue');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [draftAccounts, setDraftAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [startupNotifyConfig, setStartupNotifyConfig] = useState({
    enabled: false,
    bot_id: '',
    chat_id: '',
    message_text: '',
    image_url: '',
    image_name: '',
    file_url: '',
    file_name: '',
    file_mime: '',
  });
  const [startupNotifyInfo, setStartupNotifyInfo] = useState('');
  const [onecReadyNotifyConfig, setOnecReadyNotifyConfig] = useState({
    enabled: false,
    bot_id: '',
    chat_id: '',
    message_text: '',
    image_url: '',
    image_name: '',
    file_url: '',
    file_name: '',
    file_mime: '',
  });
  const [onecReadyNotifyInfo, setOnecReadyNotifyInfo] = useState('');
  const [datacenterLinkNotifyConfig, setDatacenterLinkNotifyConfig] = useState({
    enabled: false,
    bot_id: '',
    chat_id: '',
    message_text: '',
    image_url: '',
    image_name: '',
    file_url: '',
    file_name: '',
    file_mime: '',
  });
  const [datacenterLinkNotifyInfo, setDatacenterLinkNotifyInfo] = useState('');
  const [officeLinkProbeConfig, setOfficeLinkProbeConfig] = useState({
    enabled: true,
    secret: '',
    has_secret: false,
  });
  const [userFallbackConfig, setUserFallbackConfig] = useState({
    enabled: false,
    api_id: '',
    api_hash: '',
    has_api_hash: false,
    api_hash_hint: null,
    phone: '',
    session_name: 'session_name',
    python_cmd: 'py',
    has_session_file: false,
  });
  const [telethonAuthCode, setTelethonAuthCode] = useState('');
  const [telethonAuthPassword, setTelethonAuthPassword] = useState('');
  const [telethonAuthInfo, setTelethonAuthInfo] = useState('');
  const [telethonNeeds2fa, setTelethonNeeds2fa] = useState(false);
  const [telethonCodeRequested, setTelethonCodeRequested] = useState(false);
  const [telethonSessionStatus, setTelethonSessionStatus] = useState(null);
  const [telethonTestChatId, setTelethonTestChatId] = useState('');
  const [telethonBusy, setTelethonBusy] = useState('');
  /** Готовый JSON для ПК с watchdog — показываем только сразу после сохранения нового ключа. */
  const [officeWatchdogBundleText, setOfficeWatchdogBundleText] = useState('');
  const [officeWatchdogLabel, setOfficeWatchdogLabel] = useState('Office-PC');
  /** Если открыли админку не с публичного домена — подставьте https://messendger.fun */
  const [officeWatchdogBaseUrl, setOfficeWatchdogBaseUrl] = useState('');
  const [showTokenMap, setShowTokenMap] = useState({});

  const [newTpl, setNewTpl] = useState({
    bot_id: '',
    title: '',
    body_text: '',
    image_url: '',
    image_name: '',
    file_url: '',
    file_name: '',
    file_mime: '',
    attachments: [],
    schedule_recurrence: 'none',
    schedule_weekday: 1,
    schedule_time: '09:00',
    scheduled_local: '',
  });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  /** Только шаблоны — без полноэкранной «Загрузка…» и без повторного запроса аккаунтов (быстрее удаление/правки). */
  const loadTemplatesOnly = useCallback(async () => {
    const rt = await fetch('/api/telegram-bots/templates', { headers: authHeaders() });
    if (!rt.ok) return false;
    const jt = await rt.json();
    setTemplates(Array.isArray(jt.templates) ? jt.templates : []);
    return true;
  }, [authHeaders]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ra, rt] = await Promise.all([
        fetch('/api/telegram-bots/accounts', { headers: authHeaders() }),
        fetch('/api/telegram-bots/templates', { headers: authHeaders() }),
      ]);
      const rc = await fetch('/api/telegram-bots/startup-notify-config', { headers: authHeaders() });
      const r1c = await fetch('/api/telegram-bots/onec-ready-notify-config', { headers: authHeaders() });
      const rdc = await fetch('/api/telegram-bots/datacenter-link-restored-notify-config', { headers: authHeaders() });
      const roffice = await fetch('/api/telegram-bots/office-link-probe-config', { headers: authHeaders() });
      const ruf = await fetch('/api/telegram-bots/user-fallback-config', { headers: authHeaders() });
      if (!ra.ok) throw new Error('Не удалось загрузить ботов');
      if (!rt.ok) throw new Error('Не удалось загрузить шаблоны');
      if (!rc.ok) throw new Error('Не удалось загрузить настройки уведомления о старте сервера');
      if (!r1c.ok) throw new Error('Не удалось загрузить настройки уведомления по серверу 1С');
      if (!rdc.ok) throw new Error('Не удалось загрузить настройки уведомления о восстановлении канала ЦОД');
      if (!roffice.ok) throw new Error('Не удалось загрузить настройки ключа office-link probe');
      if (!ruf.ok) throw new Error('Failed to load Telegram fallback settings');
      const ja = await ra.json();
      const jt = await rt.json();
      const jc = await rc.json();
      const j1c = await r1c.json();
      const jdc = await rdc.json();
      const jOffice = await roffice.json();
      const jUf = await ruf.json();
      const acc = Array.isArray(ja.accounts) ? ja.accounts : [];
      setAccounts(acc);
      setDraftAccounts(
        acc.map((a) => ({
          id: a.id,
          label: a.label || '',
          chat_id: a.chat_id || '',
          bot_token: '',
          shared: !!a.shared,
        }))
      );
      setTemplates(Array.isArray(jt.templates) ? jt.templates : []);
      const cfg = jc?.config || {};
      setStartupNotifyConfig({
        enabled: cfg.enabled === true,
        bot_id: cfg.bot_id != null ? String(cfg.bot_id) : '',
        chat_id: cfg.chat_id || '',
        message_text: cfg.message_text || '',
        image_url: cfg.image_url || '',
        image_name: cfg.image_name || '',
        file_url: cfg.file_url || '',
        file_name: cfg.file_name || '',
        file_mime: cfg.file_mime || '',
      });
      const cfg1c = j1c?.config || {};
      setOnecReadyNotifyConfig({
        enabled: cfg1c.enabled === true,
        bot_id: cfg1c.bot_id != null ? String(cfg1c.bot_id) : '',
        chat_id: cfg1c.chat_id || '',
        message_text: cfg1c.message_text || '',
        image_url: cfg1c.image_url || '',
        image_name: cfg1c.image_name || '',
        file_url: cfg1c.file_url || '',
        file_name: cfg1c.file_name || '',
        file_mime: cfg1c.file_mime || '',
      });
      const cfgDc = jdc?.config || {};
      setDatacenterLinkNotifyConfig({
        enabled: cfgDc.enabled === true,
        bot_id: cfgDc.bot_id != null ? String(cfgDc.bot_id) : '',
        chat_id: cfgDc.chat_id || '',
        message_text: cfgDc.message_text || '',
        image_url: cfgDc.image_url || '',
        image_name: cfgDc.image_name || '',
        file_url: cfgDc.file_url || '',
        file_name: cfgDc.file_name || '',
        file_mime: cfgDc.file_mime || '',
      });
      const officeCfg = jOffice?.config || {};
      setOfficeLinkProbeConfig({
        enabled: officeCfg.enabled !== false,
        secret: '',
        has_secret: officeCfg.has_secret === true,
      });
      const ufCfg = jUf?.config || {};
      setUserFallbackConfig({
        enabled: ufCfg.enabled === true,
        api_id: ufCfg.api_id != null ? String(ufCfg.api_id) : '',
        api_hash: '',
        has_api_hash: ufCfg.has_api_hash === true,
        api_hash_hint: ufCfg.api_hash_hint || null,
        phone: ufCfg.phone || '',
        session_name: ufCfg.session_name || 'session_name',
        python_cmd: ufCfg.python_cmd || 'py',
        has_session_file: ufCfg.has_session_file === true,
      });
      setTelethonSessionStatus(jUf?.session_status || null);
      setTelethonAuthInfo('');
      setTelethonAuthCode('');
      setTelethonAuthPassword('');
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const [deletingId, setDeletingId] = useState(null);
  const swipeTouchRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (open) {
      loadAll();
      setTab('queue');
    }
  }, [open, loadAll]);

  const saveAccounts = async () => {
    setSaving(true);
    setError('');
    try {
      // Не отправляем общих ботов (shared === true) в теле «личного» сохранения
      const rowsToSave = isAdminUser ? draftAccounts : draftAccounts.filter((d) => d.shared !== true);
      const payload = rowsToSave.map((d) => ({
        id: d.id,
        label: d.label,
        chat_id: d.chat_id,
        bot_token: d.bot_token,
        ...(isAdminUser ? { shared: !!d.shared } : {}),
      }));
      const r = await fetch('/api/telegram-bots/accounts', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ accounts: payload }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Сохранение не удалось');
      const acc = Array.isArray(j.accounts) ? j.accounts : [];
      setAccounts(acc);
      setDraftAccounts(
        acc.map((a) => ({
          id: a.id,
          label: a.label || '',
          chat_id: a.chat_id || '',
          bot_token: '',
          shared: !!a.shared,
        }))
      );
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const saveStartupNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setStartupNotifyInfo('');
    try {
      const payload = {
        enabled: startupNotifyConfig.enabled === true,
        bot_id: startupNotifyConfig.bot_id ? Number(startupNotifyConfig.bot_id) : null,
        chat_id: startupNotifyConfig.chat_id || '',
        message_text: startupNotifyConfig.message_text || '',
        image_url: startupNotifyConfig.image_url || '',
        image_name: startupNotifyConfig.image_name || '',
        file_url: startupNotifyConfig.file_url || '',
        file_name: startupNotifyConfig.file_name || '',
        file_mime: startupNotifyConfig.file_mime || '',
      };
      const r = await fetch('/api/telegram-bots/startup-notify-config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось сохранить настройки уведомления');
      const cfg = j?.config || payload;
      setStartupNotifyConfig({
        enabled: cfg.enabled === true,
        bot_id: cfg.bot_id != null ? String(cfg.bot_id) : '',
        chat_id: cfg.chat_id || '',
        message_text: cfg.message_text || '',
        image_url: cfg.image_url || '',
        image_name: cfg.image_name || '',
        file_url: cfg.file_url || '',
        file_name: cfg.file_name || '',
        file_mime: cfg.file_mime || '',
      });
      setStartupNotifyInfo('Настройки сохранены. Сообщение уйдёт в Telegram после перезапуска службы мессенджера.');
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const testStartupNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setStartupNotifyInfo('');
    try {
      const r = await fetch('/api/telegram-bots/startup-notify-config/test', {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось отправить тест');
      setStartupNotifyInfo(j.message || 'Тестовое сообщение отправлено в Telegram');
    } catch (e) {
      setError(e.message || 'Ошибка тестовой отправки');
    } finally {
      setSaving(false);
    }
  };

  const saveOnecReadyNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setOnecReadyNotifyInfo('');
    try {
      const payload = {
        enabled: onecReadyNotifyConfig.enabled === true,
        bot_id: onecReadyNotifyConfig.bot_id ? Number(onecReadyNotifyConfig.bot_id) : null,
        chat_id: onecReadyNotifyConfig.chat_id || '',
        message_text: onecReadyNotifyConfig.message_text || '',
        image_url: onecReadyNotifyConfig.image_url || '',
        image_name: onecReadyNotifyConfig.image_name || '',
        file_url: onecReadyNotifyConfig.file_url || '',
        file_name: onecReadyNotifyConfig.file_name || '',
        file_mime: onecReadyNotifyConfig.file_mime || '',
      };
      const r = await fetch('/api/telegram-bots/onec-ready-notify-config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось сохранить настройки уведомления 1С');
      const cfg = j?.config || payload;
      setOnecReadyNotifyConfig({
        enabled: cfg.enabled === true,
        bot_id: cfg.bot_id != null ? String(cfg.bot_id) : '',
        chat_id: cfg.chat_id || '',
        message_text: cfg.message_text || '',
        image_url: cfg.image_url || '',
        image_name: cfg.image_name || '',
        file_url: cfg.file_url || '',
        file_name: cfg.file_name || '',
        file_mime: cfg.file_mime || '',
      });
      setOnecReadyNotifyInfo(
        'Настройки сохранены. Telegram-оповещение уйдёт, когда монитор 1С зафиксирует готовность (SQL + 1С в норме).'
      );
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const testOnecReadyNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setOnecReadyNotifyInfo('');
    try {
      const r = await fetch('/api/telegram-bots/onec-ready-notify-config/test', {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось отправить тест');
      setOnecReadyNotifyInfo(j.message || 'Тестовое сообщение отправлено в Telegram');
    } catch (e) {
      setError(e.message || 'Ошибка тестовой отправки');
    } finally {
      setSaving(false);
    }
  };

  const saveDatacenterLinkNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setDatacenterLinkNotifyInfo('');
    try {
      const payload = {
        enabled: datacenterLinkNotifyConfig.enabled === true,
        bot_id: datacenterLinkNotifyConfig.bot_id ? Number(datacenterLinkNotifyConfig.bot_id) : null,
        chat_id: datacenterLinkNotifyConfig.chat_id || '',
        message_text: datacenterLinkNotifyConfig.message_text || '',
        image_url: datacenterLinkNotifyConfig.image_url || '',
        image_name: datacenterLinkNotifyConfig.image_name || '',
        file_url: datacenterLinkNotifyConfig.file_url || '',
        file_name: datacenterLinkNotifyConfig.file_name || '',
        file_mime: datacenterLinkNotifyConfig.file_mime || '',
      };
      const r = await fetch('/api/telegram-bots/datacenter-link-restored-notify-config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось сохранить настройки уведомления ЦОД');
      const cfg = j?.config || payload;
      setDatacenterLinkNotifyConfig({
        enabled: cfg.enabled === true,
        bot_id: cfg.bot_id != null ? String(cfg.bot_id) : '',
        chat_id: cfg.chat_id || '',
        message_text: cfg.message_text || '',
        image_url: cfg.image_url || '',
        image_name: cfg.image_name || '',
        file_url: cfg.file_url || '',
        file_name: cfg.file_name || '',
        file_mime: cfg.file_mime || '',
      });
      setDatacenterLinkNotifyInfo(
        'Настройки сохранены. Уведомление сработает только при восстановлении исходящего интернета на сервере мессенджера (watchdog).'
      );
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const testDatacenterLinkNotifyConfig = async () => {
    setSaving(true);
    setError('');
    setDatacenterLinkNotifyInfo('');
    try {
      const r = await fetch('/api/telegram-bots/datacenter-link-restored-notify-config/test', {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось отправить тест');
      setDatacenterLinkNotifyInfo(j.message || 'Тестовое сообщение отправлено в Telegram');
    } catch (e) {
      setError(e.message || 'Ошибка тестовой отправки');
    } finally {
      setSaving(false);
    }
  };

  const applyUserFallbackResponse = (j) => {
    if (j?.session_status) setTelethonSessionStatus(j.session_status);
    const ufCfg = j?.config;
    if (!ufCfg || typeof ufCfg !== 'object') return;
    setUserFallbackConfig((p) => ({
      ...p,
      enabled: ufCfg.enabled === true ? true : ufCfg.enabled === false ? false : p.enabled,
      api_id: ufCfg.api_id != null ? String(ufCfg.api_id) : p.api_id,
      api_hash: '',
      has_api_hash: ufCfg.has_api_hash === true ? true : ufCfg.has_api_hash === false ? false : p.has_api_hash,
      api_hash_hint: ufCfg.api_hash_hint != null ? ufCfg.api_hash_hint : p.api_hash_hint,
      phone: ufCfg.phone || p.phone,
      session_name: ufCfg.session_name || p.session_name,
      python_cmd: ufCfg.python_cmd || p.python_cmd,
      has_session_file: ufCfg.has_session_file === true,
    }));
  };

  const saveUserFallbackConfig = async () => {
    setSaving(true);
    setError('');
    setTelethonAuthInfo('');
    try {
      const payload = {
        enabled: userFallbackConfig.enabled === true,
        api_id: userFallbackConfig.api_id,
        api_hash: userFallbackConfig.api_hash || undefined,
        phone: userFallbackConfig.phone,
        session_name: userFallbackConfig.session_name,
        python_cmd: userFallbackConfig.python_cmd,
      };
      const r = await fetch('/api/telegram-bots/user-fallback-config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Failed to save');
      applyUserFallbackResponse(j);
      const st = j.session_status;
      const hasFile = j.config?.has_session_file === true;
      const authorized = st?.authorized === true;
      let hint = j.message || 'Сохранено';
      if (Array.isArray(j.warnings) && j.warnings.length) {
        hint = `${j.warnings.join(' ')} ${hint}`;
      }
      if (authorized) {
        hint += ' Сессия активна — запасная отправка работает.';
      } else if (hasFile) {
        hint +=
          ' На сервере старый файл сессии. Для нового аккаунта: Сбросить сессию → Отправить код → Подтвердить код.';
      } else {
        hint += ' Файл сессии появится после успешного входа по коду.';
      }
      setTelethonAuthInfo(hint);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const refreshTelethonSessionStatus = async () => {
    try {
      const r = await fetch('/api/telegram-bots/user-fallback/session-status', { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Check failed');
      setTelethonSessionStatus(j.session_status || null);
      if (j.config) applyUserFallbackResponse(j);
    } catch (e) {
      setError(e.message || 'Error');
    }
  };

  const checkTelethonPreflight = async () => {
    setTelethonBusy('preflight');
    setError('');
    setTelethonAuthInfo('');
    try {
      const r = await fetch('/api/telegram-bots/user-fallback/preflight', { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Preflight check failed');
      setTelethonAuthInfo(j.message || 'Server is ready to send code');
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setTelethonBusy('');
    }
  };

  const TELETHON_FETCH_MS = 120000;

  const fetchTelethonApi = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TELETHON_FETCH_MS);
    try {
      const r = await fetch(url, { ...options, signal: controller.signal });
      const j = await r.json().catch(() => ({}));
      return { r, j };
    } catch (e) {
      if (e && e.name === 'AbortError') {
        throw new Error('Таймаут 2 мин — сервер не ответил. Проверьте Python (кнопка «Проверить Python») и перезапуск Node.');
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }
  };

  const buildTelethonConfigPayload = () => ({
    enabled: userFallbackConfig.enabled === true,
    api_id: userFallbackConfig.api_id,
    api_hash: userFallbackConfig.api_hash || undefined,
    phone: userFallbackConfig.phone,
    session_name: userFallbackConfig.session_name,
    python_cmd: userFallbackConfig.python_cmd,
  });

  const formatTelethonCodeSuccess = (j) => {
    const info = ['✅ Код запрошен в Telegram. Сохраните настройки не нужно — всё уже на сервере.'];
    if (j.sent_at) {
      info.push(`Время: ${new Date(j.sent_at).toLocaleString('ru-RU')}.`);
    }
    if (j.code_type === 'SentCodeTypeApp') {
      info.push(
        'Код приходит во всплывающее уведомление Telegram или Telegram Desktop — часто НЕ в чат «Telegram» на телефоне.'
      );
    } else if (j.code_type) {
      info.push(`Доставка: ${j.code_type}.`);
    }
    if (j.hint) info.push(j.hint);
    info.push('Введите код ниже и нажмите «Подтвердить код». Повторно «Получить код» — только если кода нет.');
    return info.join(' ');
  };

  const sendTelethonAuthCode = async (forceSms = false) => {
    const busyKey = forceSms ? 'send-code-sms' : 'send-code';
    setTelethonBusy(busyKey);
    setError('');
    setTelethonAuthInfo(
      forceSms
        ? '⏳ Сохраняю настройки и запрашиваю SMS… подождите до 2 минут.'
        : '⏳ Сохраняю настройки и отправляю запрос в Telegram… подождите до 2 минут (не закрывайте окно).'
    );
    try {
      if (!String(userFallbackConfig.api_id || '').trim()) {
        throw new Error('Укажите api_id');
      }
      if (!userFallbackConfig.has_api_hash && !String(userFallbackConfig.api_hash || '').trim()) {
        throw new Error('Введите api_hash с my.telegram.org (для нового приложения — новый hash)');
      }
      if (!String(userFallbackConfig.phone || '').trim()) {
        throw new Error('Укажите телефон в формате +380...');
      }
      const { r, j } = await fetchTelethonApi('/api/telegram-bots/user-fallback/auth/request-code', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...buildTelethonConfigPayload(),
          force_sms: forceSms === true,
        }),
      });
      if (!r.ok) {
        const parts = [j.error || 'Не удалось отправить код'];
        if (j.wait_seconds) parts.push(`Подождите ${j.wait_seconds} сек.`);
        if (j.hint) parts.push(j.hint);
        const msg = parts.join(' ');
        setTelethonAuthInfo(msg);
        throw new Error(msg);
      }
      setTelethonAuthInfo(formatTelethonCodeSuccess(j));
      setTelethonNeeds2fa(false);
      setTelethonAuthPassword('');
      setTelethonCodeRequested(true);
      applyUserFallbackResponse(j);
    } catch (e) {
      setTelethonAuthInfo(e.message || 'Ошибка');
      setError(e.message || 'Ошибка');
    } finally {
      setTelethonBusy('');
    }
  };

  const confirmTelethonAuthCode = async () => {
    const codeDigits = String(telethonAuthCode || '').replace(/\D/g, '').trim();
    const pwd = String(telethonAuthPassword || '').trim();

    if (telethonNeeds2fa && !pwd) {
      setTelethonAuthInfo('Шаг 3: введите облачный пароль из Telegram и снова нажмите «Войти».');
      setError('Нужен облачный пароль');
      return;
    }
    if (!codeDigits && !(telethonNeeds2fa && pwd)) {
      setTelethonAuthInfo('Шаг 2: введите цифры из сообщения Telegram («Код для входа: 12345»).');
      setError('Нужен код из Telegram');
      return;
    }

    setTelethonBusy('confirm');
    setError('');
    setTelethonAuthInfo('⏳ Вход… подождите до 2 минут (не закрывайте окно).');
    try {
      const { r, j } = await fetchTelethonApi('/api/telegram-bots/user-fallback/auth/confirm', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          code: codeDigits || undefined,
          password: pwd || undefined,
        }),
      });
      if (!r.ok) {
        if (j.need_password) {
          setTelethonNeeds2fa(true);
          setTelethonCodeRequested(true);
          const msg =
            j.error ||
            'Шаг 2 выполнен (код принят). Шаг 3: введите облачный пароль из Telegram → снова «Войти».';
          setTelethonAuthInfo(msg);
          setError(msg);
          return;
        }
        const msg = j.error || 'Не удалось войти';
        setTelethonAuthInfo(msg);
        throw new Error(msg);
      }
      setTelethonNeeds2fa(false);
      applyUserFallbackResponse(j);
      const authorized = j.session_status?.authorized === true;
      const who = j.session_status?.user?.username
        ? `@${j.session_status.user.username}`
        : j.user?.username
          ? `@${j.user.username}`
          : '';
      if (authorized) {
        setTelethonAuthInfo(
          `✅ Готово! Сессия активна${who ? ` (${who})` : ''}. Запланированные сообщения могут уходить с личного аккаунта, если бот не может писать в группу.`
        );
        setTelethonAuthCode('');
        setTelethonAuthPassword('');
      } else {
        setTelethonAuthInfo(
          `⚠️ ${j.message || 'Ответ получен'}, но сессия ещё не authorized. Нажмите «Проверить сессию» или повторите вход.`
        );
        setError('Сессия не подтвердилась — нажмите «Проверить сессию»');
        await refreshTelethonSessionStatus();
      }
    } catch (e) {
      if (!String(telethonAuthInfo || '').startsWith('✅')) {
        setTelethonAuthInfo(e.message || 'Ошибка входа');
      }
      setError(e.message || 'Ошибка');
    } finally {
      setTelethonBusy('');
    }
  };

  const resetTelethonSession = async () => {
    if (
      !window.confirm(
        'Удалить файл сессии на сервере? После этого: «Отправить код» → ввести код из Telegram → «Подтвердить код».'
      )
    )
      return;
    setTelethonBusy('reset');
    setError('');
    try {
      const r = await fetch('/api/telegram-bots/user-fallback/reset-session', {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Reset failed');
      setTelethonNeeds2fa(false);
      setTelethonAuthPassword('');
      setTelethonCodeRequested(false);
      setTelethonAuthInfo(j.message || 'Сессия сброшена. Начните с шага 1 — «Получить код».');
      await refreshTelethonSessionStatus();
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setTelethonBusy('');
    }
  };

  const testTelethonSend = async () => {
    setTelethonBusy('test');
    setError('');
    try {
      const r = await fetch('/api/telegram-bots/user-fallback/test-send', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          chat_id: telethonTestChatId,
          text: 'Messenger fallback send test',
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Send failed');
      setTelethonAuthInfo(j.message || 'Sent');
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setTelethonBusy('');
    }
  };

  const telethonControlsDisabled = !!telethonBusy;

  const saveOfficeLinkProbeConfig = async () => {
    setSaving(true);
    setError('');
    setOfficeWatchdogBundleText('');
    try {
      const payload = {
        enabled: officeLinkProbeConfig.enabled !== false,
        secret: officeLinkProbeConfig.secret || '',
      };
      const r = await fetch('/api/telegram-bots/office-link-probe-config', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось сохранить ключ office-link probe');
      const once = typeof j?.secret_download_once === 'string' ? j.secret_download_once : '';
      if (once) {
        const rawBase = (officeWatchdogBaseUrl || '').trim() || (typeof window !== 'undefined' ? window.location.origin : '');
        const base = rawBase.replace(/\/$/, '');
        const bundle = {
          OFFICE_LINK_PROBE_URL: `${base}/api/public/datacenter-access-beat`,
          OFFICE_LINK_PROBE_KEY: once,
          OFFICE_LINK_OFFICE_LABEL: (officeWatchdogLabel || 'Office-PC').trim().slice(0, 120) || 'Office-PC',
          OFFICE_LINK_PROBE_INTERVAL_SEC: '20',
          OFFICE_LINK_PROBE_TIMEOUT_SEC: '8',
        };
        setOfficeWatchdogBundleText(`${JSON.stringify(bundle, null, 2)}\n`);
      }
      setOfficeLinkProbeConfig((p) => ({
        ...p,
        enabled: j?.config?.enabled !== false,
        has_secret: j?.config?.has_secret === true,
        secret: '',
      }));
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const downloadOfficeWatchdogJson = () => {
    if (!officeWatchdogBundleText) return;
    const blob = new Blob([officeWatchdogBundleText], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'office-link-watchdog.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const sharedCount = draftAccounts.filter((d) => d.shared).length;
  const personalCount = draftAccounts.filter((d) => !d.shared).length;
  const selectedAccountForNewTpl = accounts.find((a) => String(a.id) === String(newTpl.bot_id));
  const newTplBotMissingChatId =
    Boolean(newTpl.bot_id) &&
    selectedAccountForNewTpl &&
    !String(selectedAccountForNewTpl.chat_id || '').trim();

  const addPersonalBotSlot = () => {
    if (personalCount >= MAX_BOTS) return;
    setDraftAccounts((prev) => [...prev, { id: null, label: '', chat_id: '', bot_token: '', shared: false }]);
  };

  const addSharedBotSlot = () => {
    if (!isAdminUser || sharedCount >= MAX_BOTS) return;
    setDraftAccounts((prev) => [...prev, { id: null, label: '', chat_id: '', bot_token: '', shared: true }]);
  };

  const removeBotSlot = (index) => {
    if (!isAdminUser && draftAccounts[index]?.shared) return;
    setDraftAccounts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDraft = (index, field, value) => {
    setDraftAccounts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const toggleTokenVisibility = (index) => {
    setShowTokenMap((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const createTemplate = async () => {
    setSaving(true);
    setError('');
    try {
      const bot_id = Number(newTpl.bot_id);
      if (!bot_id) throw new Error('Выберите бота');
      const onceIso = fromDatetimeLocalToIso(newTpl.scheduled_local);
      let rec = newTpl.schedule_recurrence || 'none';
      // Частая ошибка: заполнили дату/время, но в списке осталось «Без расписания» — на сервер уходило без scheduled_at
      if (rec === 'none' && onceIso) rec = 'once';
      if (rec === 'once' && !onceIso) {
        throw new Error('Укажите дату и время для однократной отправки (и при необходимости выберите «Один раз» в расписании)');
      }
      if ((rec === 'weekly' || rec === 'weekdays' || rec === 'weekends') && !String(newTpl.schedule_time || '').trim()) {
        throw new Error('Укажите время отправки');
      }
      const body = {
        bot_id,
        title: newTpl.title,
        body_text: newTpl.body_text,
        image_url: newTpl.image_url || null,
        image_name: newTpl.image_name || null,
        file_url: newTpl.file_url || null,
        file_name: newTpl.file_name || null,
        file_mime: newTpl.file_mime || null,
        attachments_json: JSON.stringify(Array.isArray(newTpl.attachments) ? newTpl.attachments : []),
        schedule_recurrence: rec,
        schedule_weekday: rec === 'weekly' ? Number(newTpl.schedule_weekday) : null,
        schedule_time:
          rec === 'weekly' || rec === 'weekdays' || rec === 'weekends' ? newTpl.schedule_time : null,
        scheduled_at: rec === 'once' ? onceIso : null,
      };
      const r = await fetch('/api/telegram-bots/templates', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось создать');
      setNewTpl({
        bot_id: '',
        title: '',
        body_text: '',
        image_url: '',
        image_name: '',
        file_url: '',
        file_name: '',
        file_mime: '',
        attachments: [],
        schedule_recurrence: 'none',
        schedule_weekday: 1,
        schedule_time: '09:00',
        scheduled_local: '',
      });
      await loadTemplatesOnly();
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = useCallback(
    async (id) => {
      if (!window.confirm('Удалить этот шаблон?')) return;
      setDeletingId(id);
      setError('');
      setTemplates((p) => p.filter((x) => x.id !== id));
      try {
        const r = await fetch(`/api/telegram-bots/templates/${id}`, {
          method: 'DELETE',
          headers: { Authorization: authHeaders().Authorization },
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || 'Не удалось удалить');
        }
      } catch (e) {
        await loadTemplatesOnly();
        setError(e.message || 'Ошибка');
      } finally {
        setDeletingId(null);
      }
    },
    [authHeaders, loadTemplatesOnly]
  );

  const patchTemplate = useCallback(
    async (id, body) => {
      const keys = Object.keys(body || {});
      const onlyEnabled = keys.length === 1 && keys[0] === 'enabled';
      if (!onlyEnabled) {
        setSaving(true);
        setError('');
      } else {
        setError('');
        setTemplates((prev) =>
          prev.map((x) => (x.id === id ? { ...x, enabled: body.enabled ? 1 : 0 } : x))
        );
      }
      try {
        const r = await fetch(`/api/telegram-bots/templates/${id}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || 'Не удалось сохранить');
        if (!onlyEnabled) await loadTemplatesOnly();
      } catch (e) {
        await loadTemplatesOnly();
        setError(e.message || 'Ошибка');
        throw e;
      } finally {
        if (!onlyEnabled) setSaving(false);
      }
    },
    [authHeaders, loadTemplatesOnly]
  );

  const swipeHandlersFor = useCallback(
    (templateId) =>
      !isMobile
        ? undefined
        : {
            onTouchStart: (e) => {
              swipeTouchRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
              };
            },
            onTouchEnd: (e) => {
              const te = e.changedTouches[0];
              const dx = te.clientX - swipeTouchRef.current.x;
              const dy = Math.abs(te.clientY - swipeTouchRef.current.y);
              if (dy > 55) return;
              if (Math.abs(dx) < 100) return;
              deleteTemplate(templateId);
            },
          },
    [isMobile, deleteTemplate]
  );

  const toggleTemplateEnabled = async (id, next) => {
    try {
      await patchTemplate(id, { enabled: next });
    } catch {
      /* ошибка в patchTemplate */
    }
  };

  if (!open) return null;

  const uploadTemplateImage = async (file) => {
    const form = new FormData();
    form.append('image', file);
    const token = localStorage.getItem('token');
    const r = await fetch('/api/telegram-bots/templates/upload-image', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Не удалось загрузить изображение');
    return {
      image_url: j.image_url || '',
      image_name: j.image_name || file.name || '',
    };
  };

  const uploadTemplateFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    const r = await fetch('/api/telegram-bots/templates/upload-file', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'Не удалось загрузить файл');
    return {
      file_url: j.file_url || '',
      file_name: j.file_name || file.name || '',
      file_mime: j.file_mime || file.type || '',
    };
  };

  const appendNewTemplateAttachment = (att) => {
    setNewTpl((p) => ({
      ...p,
      attachments: [...(Array.isArray(p.attachments) ? p.attachments : []), att].slice(0, 12),
    }));
  };

  const deskOff = !isMobile ? sidebarOffset : 0;
  const overlayDesktop = !isMobile && deskOff > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="telegram-bots-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: overlayDesktop ? deskOff : 0,
        right: 0,
        bottom: 0,
        width: overlayDesktop ? `calc(100vw - ${deskOff}px)` : '100vw',
        height: '100vh',
        zIndex: 650010,
        background: overlayDesktop ? 'rgba(0,0,0,0.55)' : 'rgba(15, 23, 42, 0.35)',
        backdropFilter: overlayDesktop ? 'none' : 'blur(4px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: 0,
        boxSizing: 'border-box',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          background: 'linear-gradient(180deg, #e0f2fe 0%, #e0e7ff 40%, #f0fdfa 100%)',
          borderRadius: 0,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 'none',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            background: 'linear-gradient(135deg, #0284c7 0%, #4f46e5 55%, #0d9488 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 8px 32px rgba(15, 23, 42, 0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }}
            >
              <FaTelegramPlane size={24} />
            </div>
            <div>
              <h2 id="telegram-bots-title" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#ffffff' }}>
                Telegram-бот
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
                Уведомления и расписание
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              border: '2px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 12,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
            }}
          >
            <FiX size={22} />
          </button>
        </div>

        <div style={{ padding: '14px 16px 0', background: 'transparent' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
              gap: 10,
              padding: 10,
              background: 'rgba(255,255,255,0.65)',
              borderRadius: 16,
              border: '1px solid #bae6fd',
              boxShadow: '0 4px 18px rgba(14, 165, 233, 0.15)',
            }}
          >
            <TabPill active={tab === 'settings'} onClick={() => setTab('settings')}>
              Настройки бота
            </TabPill>
            <TabPill active={tab === 'templates'} onClick={() => setTab('templates')}>
              Шаблоны бота
            </TabPill>
            <TabPill active={tab === 'queue'} onClick={() => setTab('queue')}>
              В работе
            </TabPill>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {error ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#b91c1c',
                fontSize: 14,
              }}
            >
              {error}
            </div>
          ) : null}

          {loading ? (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>Загрузка…</div>
          ) : tab === 'settings' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
                <strong>Общие боты</strong> (настраивает администратор) видны всем при выборе в «Шаблоны бота».{' '}
                <strong>Свои боты</strong> — до {MAX_BOTS} личных записей с токеном и chat_id; шаблоны у каждого только свои.
              </p>
              {draftAccounts.map((row, idx) => {
                const existed = row.id != null;
                const orig = accounts.find((a) => a.id === row.id);
                const readOnlyShared = row.shared && !isAdminUser;
                return (
                  <div
                    key={row.id ?? `${row.shared ? 's' : 'p'}-${idx}`}
                    style={{
                      ...cardShell,
                      padding: 16,
                      opacity: readOnlyShared ? 0.92 : 1,
                      borderLeft: row.shared ? '4px solid #6366f1' : cardShell.borderLeft,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
                        {row.shared ? 'Общий бот' : 'Мой бот'}
                        {orig?.has_token ? (
                          <span style={{ fontWeight: 500, color: '#22c55e', marginLeft: 8, fontSize: 13 }}>токен сохранён</span>
                        ) : null}
                      </span>
                      {(isAdminUser || !row.shared) ? (
                        <button
                          type="button"
                          onClick={() => removeBotSlot(idx)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          Удалить
                        </button>
                      ) : null}
                    </div>
                    <label style={labelStyle}>Понятное название</label>
                    <input
                      style={{ ...inputStyle, marginBottom: 12 }}
                      value={row.label}
                      onChange={(e) => updateDraft(idx, 'label', e.target.value)}
                      placeholder="Например: Алерты склада"
                      readOnly={readOnlyShared}
                      disabled={readOnlyShared}
                    />
                    <label style={labelStyle}>Токен бота</label>
                    {orig?.has_token ? (
                      <div style={{ margin: '-4px 0 8px', fontSize: 12, color: '#0f766e' }}>
                        Сохранённый токен: {orig?.token_hint || '***'}
                      </div>
                    ) : null}
                    <input
                      style={{ ...inputStyle, marginBottom: 12 }}
                      type={showTokenMap[idx] ? 'text' : 'password'}
                      autoComplete="off"
                      value={row.bot_token}
                      onChange={(e) => updateDraft(idx, 'bot_token', e.target.value)}
                      placeholder={existed ? 'Оставьте пустым, чтобы не менять' : '123456789:ABC...'}
                      readOnly={readOnlyShared}
                      disabled={readOnlyShared}
                    />
                    <div style={{ margin: '-6px 0 10px' }}>
                      <button
                        type="button"
                        onClick={() => toggleTokenVisibility(idx)}
                        disabled={readOnlyShared}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#0369a1',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: readOnlyShared ? 'not-allowed' : 'pointer',
                          padding: 0,
                        }}
                      >
                        {showTokenMap[idx] ? 'Скрыть токен' : 'Показать токен'}
                      </button>
                    </div>
                    <label style={labelStyle}>Chat ID</label>
                    <input
                      style={inputStyle}
                      value={row.chat_id}
                      onChange={(e) => updateDraft(idx, 'chat_id', e.target.value)}
                      placeholder="-1001234567890"
                      readOnly={readOnlyShared}
                      disabled={readOnlyShared}
                    />
                  </div>
                );
              })}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {isAdminUser ? (
                  <button
                    type="button"
                    disabled={sharedCount >= MAX_BOTS || saving}
                    onClick={addSharedBotSlot}
                    style={{
                      padding: '12px 18px',
                      borderRadius: 12,
                      border: '1px dashed #6366f1',
                      background: '#eef2ff',
                      color: '#4338ca',
                      fontWeight: 600,
                      cursor: sharedCount >= MAX_BOTS ? 'not-allowed' : 'pointer',
                    }}
                  >
                    + Общий бот ({sharedCount}/{MAX_BOTS})
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={personalCount >= MAX_BOTS || saving}
                  onClick={addPersonalBotSlot}
                  style={{
                    padding: '12px 18px',
                    borderRadius: 12,
                    border: '1px dashed #94a3b8',
                    background: '#fff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: personalCount >= MAX_BOTS ? 'not-allowed' : 'pointer',
                  }}
                >
                  + Мой бот ({personalCount}/{MAX_BOTS})
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveAccounts}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(14, 165, 233, 0.35)',
                  }}
                >
                  {saving ? 'Сохранение…' : 'Сохранить настройки'}
                </button>
              </div>

              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #16a34a',
                  marginTop: 12,
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>
                  Уведомление: сервер снова в сети
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                  Отправляется один раз после <strong>запуска службы мессенджера</strong> на сервере (перезагрузка Windows,
                  рестарт Node/PM2, обновление). Сотрудники получают сигнал, что удалённые рабочие столы снова доступны.
                  Это не то же самое, что блок ниже про «интернет ЦОД» — тот срабатывает при восстановлении канала связи
                  без перезапуска сервера.
                </p>

                {startupNotifyConfig.enabled && !startupNotifyConfig.bot_id ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#fef3c7',
                      color: '#92400e',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    Выберите бота и сохраните настройки — иначе уведомление не отправится.
                  </div>
                ) : null}

                {startupNotifyInfo ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#dcfce7',
                      color: '#166534',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {startupNotifyInfo}
                  </div>
                ) : null}

                <div style={{ marginBottom: 12 }}>
                  <EnableSwitch
                    on={startupNotifyConfig.enabled}
                    disabled={saving}
                    onToggle={() =>
                      setStartupNotifyConfig((p) => ({
                        ...p,
                        enabled: !p.enabled,
                      }))
                    }
                    labelOn="Включено"
                    labelOff="Выключено"
                  />
                </div>

                <label style={labelStyle}>Бот для уведомления</label>
                <select
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={startupNotifyConfig.bot_id}
                  onChange={(e) =>
                    setStartupNotifyConfig((p) => ({ ...p, bot_id: e.target.value }))
                  }
                >
                  <option value="">— выберите —</option>
                  {accounts.map((a) => {
                    const hints = [];
                    if (!a.has_token) hints.push('нет токена');
                    if (!String(a.chat_id || '').trim()) hints.push('нет chat_id');
                    const suffix = hints.length ? ` (${hints.join(', ')})` : '';
                    return (
                      <option key={a.id} value={a.id}>
                        {(a.label || `Бот #${a.id}`) + suffix}
                      </option>
                    );
                  })}
                </select>

                <label style={labelStyle}>Chat ID (опционально)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={startupNotifyConfig.chat_id}
                  onChange={(e) =>
                    setStartupNotifyConfig((p) => ({ ...p, chat_id: e.target.value }))
                  }
                  placeholder="Оставьте пустым, чтобы взять chat_id у выбранного бота"
                />

                <label style={labelStyle}>Текст сообщения</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 90, marginBottom: 12, resize: 'vertical' }}
                  value={startupNotifyConfig.message_text}
                  onChange={(e) =>
                    setStartupNotifyConfig((p) => ({ ...p, message_text: e.target.value }))
                  }
                  placeholder="Например: Сервер снова в сети, можно заходить в рабочие столы."
                />

                <label style={labelStyle}>Картинка для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <ImagePicker
                    isMobile={isMobile}
                    disabled={saving}
                    onPick={async (f) => {
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateImage(f);
                        setStartupNotifyConfig((p) => ({
                          ...p,
                          image_url: up.image_url || '',
                          image_name: up.image_name || f.name || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки картинки');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  />
                  {startupNotifyConfig.image_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <img
                        src={startupNotifyConfig.image_url}
                        alt={startupNotifyConfig.image_name || 'startup-image'}
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #bae6fd' }}
                      />
                      <span style={{ fontSize: 12, color: '#64748b' }}>{startupNotifyConfig.image_name || 'Картинка'}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setStartupNotifyConfig((p) => ({ ...p, image_url: '', image_name: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать картинку
                      </button>
                    </div>
                  ) : null}
                </div>

                <label style={labelStyle}>Файл для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateFile(f);
                        setStartupNotifyConfig((p) => ({
                          ...p,
                          file_url: up.file_url || '',
                          file_name: up.file_name || f.name || '',
                          file_mime: up.file_mime || f.type || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки файла');
                      } finally {
                        setSaving(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {startupNotifyConfig.file_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                        {startupNotifyConfig.file_name || 'Файл'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setStartupNotifyConfig((p) => ({ ...p, file_url: '', file_name: '', file_mime: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать файл
                      </button>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={saving}
                  onClick={saveStartupNotifyConfig}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #16a34a 0%, #0d9488 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(22, 163, 74, 0.35)',
                    marginRight: 10,
                  }}
                >
                  {saving ? 'Сохранение…' : 'Сохранить уведомление о старте'}
                </button>
                <button
                  type="button"
                  disabled={saving || !startupNotifyConfig.enabled}
                  onClick={testStartupNotifyConfig}
                  style={{
                    padding: '12px 18px',
                    borderRadius: 12,
                    border: '1px solid #86efac',
                    background: '#f0fdf4',
                    color: '#15803d',
                    fontWeight: 700,
                    cursor: saving || !startupNotifyConfig.enabled ? 'not-allowed' : 'pointer',
                  }}
                  title="Отправит тест в Telegram по сохранённым настройкам"
                >
                  {saving ? '…' : 'Отправить тест'}
                </button>
              </div>

              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #8b5cf6',
                  marginTop: 12,
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>
                  Уведомление: сервер 1С снова в сети
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                  Отправка в Telegram-группу, когда <strong>мониторинг 1С</strong> фиксирует готовность: SQL Server и
                  службы 1С запущены. Срабатывает после сбоя, перезагрузки Windows или долгой паузы агента. Это отдельно
                  от сообщения в чат мессенджера («Общий чат») — его можно отключить через{' '}
                  <code style={{ fontSize: 12 }}>ONEC_READY_ANNOUNCE_CHAT_ENABLED=0</code> в .env.
                </p>

                {onecReadyNotifyConfig.enabled && !onecReadyNotifyConfig.bot_id ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#fef3c7',
                      color: '#92400e',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    Выберите бота и сохраните настройки — иначе уведомление не отправится.
                  </div>
                ) : null}

                {onecReadyNotifyInfo ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#ede9fe',
                      color: '#5b21b6',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {onecReadyNotifyInfo}
                  </div>
                ) : null}

                <div style={{ marginBottom: 12 }}>
                  <EnableSwitch
                    on={onecReadyNotifyConfig.enabled}
                    disabled={saving}
                    onToggle={() =>
                      setOnecReadyNotifyConfig((p) => ({
                        ...p,
                        enabled: !p.enabled,
                      }))
                    }
                    labelOn="Включено"
                    labelOff="Выключено"
                  />
                </div>

                <label style={labelStyle}>Бот для уведомления</label>
                <select
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={onecReadyNotifyConfig.bot_id}
                  onChange={(e) =>
                    setOnecReadyNotifyConfig((p) => ({ ...p, bot_id: e.target.value }))
                  }
                >
                  <option value="">— выберите —</option>
                  {accounts.map((a) => {
                    const hints = [];
                    if (!a.has_token) hints.push('нет токена');
                    if (!String(a.chat_id || '').trim()) hints.push('нет chat_id');
                    const suffix = hints.length ? ` (${hints.join(', ')})` : '';
                    return (
                      <option key={a.id} value={a.id}>
                        {(a.label || `Бот #${a.id}`) + suffix}
                      </option>
                    );
                  })}
                </select>

                <label style={labelStyle}>Chat ID (опционально)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={onecReadyNotifyConfig.chat_id}
                  onChange={(e) =>
                    setOnecReadyNotifyConfig((p) => ({ ...p, chat_id: e.target.value }))
                  }
                  placeholder="Оставьте пустым, чтобы взять chat_id у выбранного бота"
                />

                <label style={labelStyle}>Текст сообщения</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 90, marginBottom: 12, resize: 'vertical' }}
                  value={onecReadyNotifyConfig.message_text}
                  onChange={(e) =>
                    setOnecReadyNotifyConfig((p) => ({ ...p, message_text: e.target.value }))
                  }
                  placeholder="Например: Сервер 1С снова в сети, можно запускать 1С."
                />
                <p style={{ margin: '0 0 12px', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                  Если текст пустой — в Telegram уйдёт описание события из монитора (перезагрузка, восстановление SQL и т.д.).
                </p>

                <label style={labelStyle}>Картинка для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <ImagePicker
                    isMobile={isMobile}
                    disabled={saving}
                    onPick={async (f) => {
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateImage(f);
                        setOnecReadyNotifyConfig((p) => ({
                          ...p,
                          image_url: up.image_url || '',
                          image_name: up.image_name || f.name || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки картинки');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  />
                  {onecReadyNotifyConfig.image_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <img
                        src={onecReadyNotifyConfig.image_url}
                        alt={onecReadyNotifyConfig.image_name || 'onec-ready-image'}
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #bae6fd' }}
                      />
                      <span style={{ fontSize: 12, color: '#64748b' }}>{onecReadyNotifyConfig.image_name || 'Картинка'}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setOnecReadyNotifyConfig((p) => ({ ...p, image_url: '', image_name: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать картинку
                      </button>
                    </div>
                  ) : null}
                </div>

                <label style={labelStyle}>Файл для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateFile(f);
                        setOnecReadyNotifyConfig((p) => ({
                          ...p,
                          file_url: up.file_url || '',
                          file_name: up.file_name || f.name || '',
                          file_mime: up.file_mime || f.type || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки файла');
                      } finally {
                        setSaving(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {onecReadyNotifyConfig.file_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                        {onecReadyNotifyConfig.file_name || 'Файл'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setOnecReadyNotifyConfig((p) => ({ ...p, file_url: '', file_name: '', file_mime: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать файл
                      </button>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={saving}
                  onClick={saveOnecReadyNotifyConfig}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                    marginRight: 10,
                  }}
                >
                  {saving ? 'Сохранение…' : 'Сохранить уведомление 1С'}
                </button>
                <button
                  type="button"
                  disabled={saving || !onecReadyNotifyConfig.enabled}
                  onClick={testOnecReadyNotifyConfig}
                  style={{
                    padding: '12px 18px',
                    borderRadius: 12,
                    border: '1px solid #c4b5fd',
                    background: '#f5f3ff',
                    color: '#6d28d9',
                    fontWeight: 700,
                    cursor: saving || !onecReadyNotifyConfig.enabled ? 'not-allowed' : 'pointer',
                  }}
                  title="Отправит тест в Telegram по сохранённым настройкам"
                >
                  {saving ? '…' : 'Отправить тест'}
                </button>
              </div>

              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #ea580c',
                  marginTop: 12,
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>
                  Уведомление: интернет ЦОД восстановлен (удалённые столы)
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                  Сообщение в Telegram, когда на <strong>сервере мессенджера в ЦОД</strong> пропадал и снова появился
                  исходящий интернет. Скрипт на вашем ноутбуке <strong>не должен</strong> вызывать это оповещение
                  (выключение или сон ноутбука — не сбой канала ЦОД).
                </p>
                <ul style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                  <li>
                    <strong style={{ color: '#334155' }}>Watchdog на сервере мессенджера</strong> — каждые ~30 с проверяет
                    исходящий интернет с сервера (Cloudflare / Google / Telegram). Если канал пропадал и вернулся — отправка в группу.
                  </li>
                </ul>

                {datacenterLinkNotifyConfig.enabled && !datacenterLinkNotifyConfig.bot_id ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#fef3c7',
                      color: '#92400e',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    Выберите бота и сохраните настройки — иначе уведомление не отправится.
                  </div>
                ) : null}

                {datacenterLinkNotifyInfo ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: '#ffedd5',
                      color: '#9a3412',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {datacenterLinkNotifyInfo}
                  </div>
                ) : null}

                <div style={{ marginBottom: 12 }}>
                  <EnableSwitch
                    on={datacenterLinkNotifyConfig.enabled}
                    disabled={saving}
                    onToggle={() =>
                      setDatacenterLinkNotifyConfig((p) => ({
                        ...p,
                        enabled: !p.enabled,
                      }))
                    }
                    labelOn="Включено"
                    labelOff="Выключено"
                  />
                </div>

                <label style={labelStyle}>Бот для уведомления</label>
                <select
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={datacenterLinkNotifyConfig.bot_id}
                  onChange={(e) =>
                    setDatacenterLinkNotifyConfig((p) => ({ ...p, bot_id: e.target.value }))
                  }
                >
                  <option value="">— выберите —</option>
                  {accounts.map((a) => {
                    const hints = [];
                    if (!a.has_token) hints.push('нет токена');
                    if (!String(a.chat_id || '').trim()) hints.push('нет chat_id');
                    const suffix = hints.length ? ` (${hints.join(', ')})` : '';
                    return (
                      <option key={a.id} value={a.id}>
                        {(a.label || `Бот #${a.id}`) + suffix}
                      </option>
                    );
                  })}
                </select>

                <label style={labelStyle}>Chat ID (опционально)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={datacenterLinkNotifyConfig.chat_id}
                  onChange={(e) =>
                    setDatacenterLinkNotifyConfig((p) => ({ ...p, chat_id: e.target.value }))
                  }
                  placeholder="Оставьте пустым, чтобы взять chat_id у выбранного бота"
                />

                <label style={labelStyle}>Текст сообщения</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 90, marginBottom: 12, resize: 'vertical' }}
                  value={datacenterLinkNotifyConfig.message_text}
                  onChange={(e) =>
                    setDatacenterLinkNotifyConfig((p) => ({ ...p, message_text: e.target.value }))
                  }
                  placeholder="Например: Связь с ЦОД восстановлена, можно заходить на удалённые рабочие столы."
                />
                <p style={{ margin: '0 0 12px', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                  При heartbeat из офиса текст с подписью ПК больше не подставляется — только ваш шаблон или текст watchdog.
                </p>

                <label style={labelStyle}>Картинка для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <ImagePicker
                    isMobile={isMobile}
                    disabled={saving}
                    onPick={async (f) => {
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateImage(f);
                        setDatacenterLinkNotifyConfig((p) => ({
                          ...p,
                          image_url: up.image_url || '',
                          image_name: up.image_name || f.name || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки картинки');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  />
                  {datacenterLinkNotifyConfig.image_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <img
                        src={datacenterLinkNotifyConfig.image_url}
                        alt={datacenterLinkNotifyConfig.image_name || 'dc-link-image'}
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #fed7aa' }}
                      />
                      <span style={{ fontSize: 12, color: '#64748b' }}>{datacenterLinkNotifyConfig.image_name || 'Картинка'}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setDatacenterLinkNotifyConfig((p) => ({ ...p, image_url: '', image_name: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать картинку
                      </button>
                    </div>
                  ) : null}
                </div>

                <label style={labelStyle}>Файл для уведомления (опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx,.doc,.docx,.pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const up = await uploadTemplateFile(f);
                        setDatacenterLinkNotifyConfig((p) => ({
                          ...p,
                          file_url: up.file_url || '',
                          file_name: up.file_name || f.name || '',
                          file_mime: up.file_mime || f.type || '',
                        }));
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки файла');
                      } finally {
                        setSaving(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {datacenterLinkNotifyConfig.file_url ? (
                    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                        {datacenterLinkNotifyConfig.file_name || 'Файл'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setDatacenterLinkNotifyConfig((p) => ({ ...p, file_url: '', file_name: '', file_mime: '' }))
                        }
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать файл
                      </button>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={saving}
                  onClick={saveDatacenterLinkNotifyConfig}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(234, 88, 12, 0.35)',
                    marginRight: 10,
                  }}
                >
                  {saving ? 'Сохранение…' : 'Сохранить уведомление «канал ЦОД»'}
                </button>
                <button
                  type="button"
                  disabled={saving || !datacenterLinkNotifyConfig.enabled}
                  onClick={testDatacenterLinkNotifyConfig}
                  style={{
                    padding: '12px 18px',
                    borderRadius: 12,
                    border: '1px solid #fdba74',
                    background: '#fff7ed',
                    color: '#c2410c',
                    fontWeight: 700,
                    cursor: saving || !datacenterLinkNotifyConfig.enabled ? 'not-allowed' : 'pointer',
                  }}
                  title="Отправит тест в Telegram по сохранённым настройкам"
                >
                  {saving ? '…' : 'Отправить тест'}
                </button>
              </div>

              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #7c3aed',
                  marginTop: 12,
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>
                  Запасная отправка (личный Telegram)
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                  Ключи:{' '}
                  <a href="https://my.telegram.org/apps" target="_blank" rel="noreferrer">
                    my.telegram.org
                  </a>
                  . Сначала сохраните настройки ниже, затем пройдите <strong>3 шага входа</strong>.
                </p>

                <div style={{ marginBottom: 12 }}>
                  <EnableSwitch
                    on={userFallbackConfig.enabled}
                    disabled={saving}
                    onToggle={() => setUserFallbackConfig((p) => ({ ...p, enabled: !p.enabled }))}
                    labelOn="On"
                    labelOff="Off"
                  />
                </div>

                <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 8, letterSpacing: '0.04em' }}>
                  НАСТРОЙКИ API (один раз)
                </div>
                <label style={labelStyle}>api_id</label>
                <input
                  style={{ ...inputStyle, marginBottom: 10 }}
                  value={userFallbackConfig.api_id}
                  onChange={(e) => setUserFallbackConfig((p) => ({ ...p, api_id: e.target.value }))}
                  placeholder="e.g. 28504887"
                />

                <label style={labelStyle}>
                  api_hash {userFallbackConfig.has_api_hash ? `(saved ${userFallbackConfig.api_hash_hint || ''})` : ''}
                </label>
                <input
                  style={{ ...inputStyle, marginBottom: 10 }}
                  type="password"
                  autoComplete="new-password"
                  value={userFallbackConfig.api_hash}
                  onChange={(e) => setUserFallbackConfig((p) => ({ ...p, api_hash: e.target.value }))}
                  placeholder={userFallbackConfig.has_api_hash ? 'Leave empty to keep saved hash' : 'From my.telegram.org'}
                />

                <label style={labelStyle}>Phone (international format)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 10 }}
                  value={userFallbackConfig.phone}
                  onChange={(e) => setUserFallbackConfig((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+380..."
                />

                <label style={labelStyle}>Session file name (no extension)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 10 }}
                  value={userFallbackConfig.session_name}
                  onChange={(e) => setUserFallbackConfig((p) => ({ ...p, session_name: e.target.value }))}
                  placeholder="session_name"
                />

                <label style={labelStyle}>Python command (usually py on Windows)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={userFallbackConfig.python_cmd}
                  onChange={(e) => setUserFallbackConfig((p) => ({ ...p, python_cmd: e.target.value }))}
                  placeholder="py"
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  <button
                    type="button"
                    disabled={telethonControlsDisabled}
                    onClick={saveUserFallbackConfig}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: saving ? 'wait' : 'pointer',
                    }}
                  >
                    {saving ? 'Сохранение…' : 'Сохранить настройки'}
                  </button>
                  <button
                    type="button"
                    disabled={telethonControlsDisabled}
                    onClick={refreshTelethonSessionStatus}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                    }}
                  >
                    Проверить сессию
                  </button>
                  <button
                    type="button"
                    disabled={telethonControlsDisabled}
                    onClick={resetTelethonSession}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1px solid #fecaca',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                    }}
                  >
                    Сбросить сессию
                  </button>
                </div>

                <div
                  style={{
                    marginBottom: 14,
                    padding: 12,
                    borderRadius: 10,
                    background: telethonSessionStatus?.authorized ? '#f0fdf4' : '#fff7ed',
                    border: `2px solid ${telethonSessionStatus?.authorized ? '#22c55e' : '#fdba74'}`,
                    fontSize: 14,
                    color: '#334155',
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>
                    {telethonSessionStatus?.authorized
                      ? '✅ Telegram подключён'
                      : '⚠️ Telegram не подключён'}
                  </div>
                  {telethonSessionStatus?.user?.username ? (
                    <div style={{ fontSize: 13 }}>Аккаунт: @{telethonSessionStatus.user.username}</div>
                  ) : null}
                  {!telethonSessionStatus?.authorized && telethonSessionStatus?.reason ? (
                    <div style={{ marginTop: 6, fontSize: 13, color: '#b45309' }}>
                      {telethonReasonRu(telethonSessionStatus.reason)}
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    marginBottom: 14,
                    padding: 14,
                    borderRadius: 12,
                    background: '#f8fafc',
                    border: '2px solid #e2e8f0',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
                    ВХОД В TELEGRAM (3 шага)
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>
                      Шаг 1. Получить код
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                      Нажмите кнопку — код придёт в <strong>уведомление Telegram</strong> или SMS (не всегда в чат
                      «Telegram»).
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button
                        type="button"
                        disabled={telethonControlsDisabled}
                        onClick={() => sendTelethonAuthCode(false)}
                        style={{
                          flex: '1 1 200px',
                          padding: '12px 16px',
                          borderRadius: 10,
                          border: 'none',
                          background:
                            telethonBusy === 'send-code'
                              ? '#93c5fd'
                              : 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: 14,
                          cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                        }}
                      >
                        {telethonBusy === 'send-code' ? '⏳ Отправка…' : '1. Получить код в Telegram'}
                      </button>
                      <button
                        type="button"
                        disabled={telethonControlsDisabled}
                        onClick={() => sendTelethonAuthCode(true)}
                        style={{
                          padding: '12px 14px',
                          borderRadius: 10,
                          border: '1px solid #fcd34d',
                          background: '#fffbeb',
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                        }}
                      >
                        {telethonBusy === 'send-code-sms' ? '…' : 'Код по SMS'}
                      </button>
                    </div>
                    {telethonCodeRequested ? (
                      <p style={{ margin: '8px 0 0', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                        ✓ Запрос отправлен — смотрите сообщение «Код для входа: 12345»
                      </p>
                    ) : null}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>
                      Шаг 2. Код из сообщения Telegram
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                      Скопируйте <strong>только цифры</strong> из текста: «Код для входа в Telegram:{' '}
                      <strong>88542</strong>». Это <em>не</em> облачный пароль.
                    </p>
                    <input
                      style={{
                        ...inputStyle,
                        marginBottom: 0,
                        fontSize: 18,
                        fontWeight: 700,
                        letterSpacing: '0.2em',
                        border: '2px solid #3b82f6',
                      }}
                      value={telethonAuthCode}
                      onChange={(e) => setTelethonAuthCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="например 88542"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: telethonNeeds2fa ? '#c2410c' : '#64748b',
                        marginBottom: 6,
                      }}
                    >
                      Шаг 3. Облачный пароль Telegram
                      {telethonNeeds2fa ? ' — обязательно сейчас' : ' — если в Telegram «Облачный пароль: Вкл.»'}
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                      Пароль из <strong>Настройки → Конфиденциальность → Облачный пароль</strong> (не код из SMS). Если
                      облачный пароль <strong>выключен</strong> — поле оставьте пустым.
                    </p>
                    <input
                      style={{
                        ...inputStyle,
                        marginBottom: 0,
                        border: telethonNeeds2fa ? '2px solid #f97316' : '2px solid #cbd5e1',
                      }}
                      type="password"
                      autoComplete="new-password"
                      value={telethonAuthPassword}
                      onChange={(e) => setTelethonAuthPassword(e.target.value)}
                      placeholder="Пароль «Облачный пароль» в Telegram"
                    />
                  </div>

                  <button
                    type="button"
                    disabled={
                      telethonControlsDisabled ||
                      (!String(telethonAuthCode || '').replace(/\D/g, '').trim() &&
                        !(telethonNeeds2fa && telethonAuthPassword.trim()))
                    }
                    onClick={confirmTelethonAuthCode}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: telethonBusy === 'confirm' ? '#86efac' : '#16a34a',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 16,
                      cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                      boxShadow: '0 4px 14px rgba(22, 163, 74, 0.35)',
                    }}
                  >
                    {telethonBusy === 'confirm' ? '⏳ Вход… до 2 мин' : 'Войти (код + пароль если нужен)'}
                  </button>
                </div>

                {telethonAuthInfo ? (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: 12,
                      borderRadius: 10,
                      background: telethonNeeds2fa
                        ? '#fff7ed'
                        : String(telethonAuthInfo).startsWith('✅')
                          ? '#f0fdf4'
                          : '#fef2f2',
                      border: `2px solid ${
                        telethonNeeds2fa
                          ? '#fdba74'
                          : String(telethonAuthInfo).startsWith('✅')
                            ? '#86efac'
                            : '#fecaca'
                      }`,
                      fontSize: 13,
                      color: telethonNeeds2fa ? '#9a3412' : String(telethonAuthInfo).startsWith('✅') ? '#166534' : '#b91c1c',
                      lineHeight: 1.55,
                      fontWeight: 600,
                    }}
                  >
                    {telethonAuthInfo}
                  </div>
                ) : null}

                <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', marginBottom: 8 }}>ПРОВЕРКА</div>
                <label style={labelStyle}>Тест в группу (chat_id)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  <input
                    style={{ ...inputStyle, flex: '1 1 200px', marginBottom: 0 }}
                    value={telethonTestChatId}
                    onChange={(e) => setTelethonTestChatId(e.target.value)}
                    placeholder="-1001585312733"
                  />
                  <button
                    type="button"
                    disabled={telethonControlsDisabled || !telethonTestChatId.trim()}
                    onClick={testTelethonSend}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      fontWeight: 700,
                      cursor: telethonControlsDisabled ? 'wait' : 'pointer',
                    }}
                  >
                    {telethonBusy === 'test' ? 'Sending…' : 'Test send'}
                  </button>
                </div>
              </div>

              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #0ea5e9',
                  marginTop: 12,
                }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>
                  Ключ office-link probe (без кода)
                </h3>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
                  Опциональный пульс с ПК в офисе на{' '}
                  <code style={{ fontSize: 12 }}>/api/public/datacenter-access-beat</code> — только для диагностики
                  (лог на сервере). <strong>Не отправляет сообщения в Telegram.</strong> Оповещение сотрудникам идёт
                  только через watchdog на сервере ЦОД (блок выше). Скрипт на ноутбуке можно оставить или отключить в
                  планировщике — на утреннее «ложное» срабатывание он больше не влияет.
                </p>

                <label style={labelStyle}>Подпись ПК (для логов / Telegram)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={officeWatchdogLabel}
                  onChange={(e) => setOfficeWatchdogLabel(e.target.value)}
                  placeholder="Например: Home-Laptop или Office-Front"
                />

                <label style={labelStyle}>Базовый URL мессенджера (опционально)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={officeWatchdogBaseUrl}
                  onChange={(e) => setOfficeWatchdogBaseUrl(e.target.value)}
                  placeholder="Пусто = текущий адрес в браузере (лучше открыть настройки с https://ваш-домен)"
                />

                <div style={{ marginBottom: 12 }}>
                  <EnableSwitch
                    on={officeLinkProbeConfig.enabled}
                    disabled={saving}
                    onToggle={() =>
                      setOfficeLinkProbeConfig((p) => ({
                        ...p,
                        enabled: !p.enabled,
                      }))
                    }
                    labelOn="Включено"
                    labelOff="Выключено"
                  />
                </div>

                <label style={labelStyle}>Новый ключ (минимум 16 символов)</label>
                <input
                  style={{ ...inputStyle, marginBottom: 8 }}
                  type="password"
                  autoComplete="new-password"
                  value={officeLinkProbeConfig.secret}
                  onChange={(e) => setOfficeLinkProbeConfig((p) => ({ ...p, secret: e.target.value }))}
                  placeholder={officeLinkProbeConfig.has_secret ? 'Ключ уже сохранён. Введите новый только если нужно заменить.' : 'Вставьте ключ'}
                />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      setOfficeLinkProbeConfig((p) => ({
                        ...p,
                        secret: generateOfficeLinkSecret(96),
                      }))
                    }
                    style={{
                      border: '1px solid #93c5fd',
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      padding: '8px 12px',
                      borderRadius: 10,
                      fontWeight: 700,
                      cursor: saving ? 'wait' : 'pointer',
                    }}
                  >
                    Сгенерировать ключ
                  </button>
                  <button
                    type="button"
                    disabled={saving || !officeLinkProbeConfig.secret}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(officeLinkProbeConfig.secret);
                      } catch (_) {
                        // ignore
                      }
                    }}
                    style={{
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#334155',
                      padding: '8px 12px',
                      borderRadius: 10,
                      fontWeight: 700,
                      cursor: saving || !officeLinkProbeConfig.secret ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Копировать
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  {officeLinkProbeConfig.has_secret
                    ? 'Текущий ключ на сервере уже сохранён.'
                    : 'Ключ пока не задан — heartbeat из офиса не пройдёт.'}
                </div>

                <button
                  type="button"
                  disabled={saving}
                  onClick={saveOfficeLinkProbeConfig}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 14px rgba(2, 132, 199, 0.35)',
                  }}
                >
                  {saving ? 'Сохранение…' : 'Сохранить ключ office-link'}
                </button>

                {officeWatchdogBundleText ? (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 14,
                      borderRadius: 12,
                      background: '#f0fdf4',
                      border: '1px solid #86efac',
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#14532d', marginBottom: 8 }}>
                      Файл для ПК с watchdog
                    </div>
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                      Сохраните как{' '}
                      <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 6 }}>
                        C:\ProgramData\Mesendger\office-link-watchdog.json
                      </code>{' '}
                      (от имени администратора) или скачайте и скопируйте на место. Затем выполните{' '}
                      <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 6 }}>
                        schtasks /Run /TN &quot;Mesendger Office Datacenter Link Watchdog&quot;
                      </code>
                      .
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={downloadOfficeWatchdogJson}
                        style={{
                          border: 'none',
                          background: '#16a34a',
                          color: '#fff',
                          padding: '10px 16px',
                          borderRadius: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Скачать office-link-watchdog.json
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(officeWatchdogBundleText);
                          } catch (_) {
                            /* noop */
                          }
                        }}
                        style={{
                          border: '1px solid #22c55e',
                          background: '#fff',
                          color: '#15803d',
                          padding: '10px 16px',
                          borderRadius: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Копировать JSON
                      </button>
                      <button
                        type="button"
                        onClick={() => setOfficeWatchdogBundleText('')}
                        style={{
                          border: '1px solid #cbd5e1',
                          background: '#fff',
                          color: '#64748b',
                          padding: '10px 16px',
                          borderRadius: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Скрыть
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={officeWatchdogBundleText}
                      style={{
                        ...inputStyle,
                        width: '100%',
                        minHeight: 160,
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : tab === 'templates' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  ...cardShell,
                  padding: 16,
                  borderLeft: '4px solid #6366f1',
                }}
              >
                <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 800, color: '#0c4a6e' }}>Новый шаблон</h3>
                <label style={labelStyle}>Бот</label>
                <select
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={newTpl.bot_id}
                  onChange={(e) => setNewTpl((p) => ({ ...p, bot_id: e.target.value }))}
                >
                  <option value="">— выберите —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.label || `Бот #${a.id}`) + (a.has_token ? '' : ' (нет токена)')}
                    </option>
                  ))}
                </select>
                {newTplBotMissingChatId ? (
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b91c1c', lineHeight: 1.45 }}>
                    У выбранного бота не сохранён chat_id. Откройте вкладку «Настройки ботов», введите ID чата/канала и нажмите «Сохранить настройки» — иначе по расписанию отправка не выполнится.
                  </p>
                ) : null}
                <label style={labelStyle}>Название шаблона</label>
                <input
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={newTpl.title}
                  onChange={(e) => setNewTpl((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Краткое имя для списка"
                />
                <label style={labelStyle}>Текст сообщения</label>
                <textarea
                  style={{ ...inputStyle, marginBottom: 12, minHeight: 100, resize: 'vertical' }}
                  value={newTpl.body_text}
                  onChange={(e) => setNewTpl((p) => ({ ...p, body_text: e.target.value }))}
                  placeholder="Текст, который уйдёт в Telegram"
                />
                <label style={labelStyle}>Картинка (PNG/JPG, опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <ImagePicker
                    isMobile={isMobile}
                    disabled={saving}
                    onPick={async (f) => {
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const img = await uploadTemplateImage(f);
                        setNewTpl((p) => ({ ...p, ...img }));
                        appendNewTemplateAttachment({
                          kind: 'image',
                          url: img.image_url || '',
                          name: img.image_name || f.name || '',
                          mime: 'image/jpeg',
                        });
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки картинки');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  />
                  {newTpl.image_url ? (
                    <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <img
                        src={newTpl.image_url}
                        alt={newTpl.image_name || 'preview'}
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #bae6fd' }}
                      />
                      <div style={{ fontSize: 12, color: '#475569' }}>
                        {newTpl.image_name || 'image'}
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewTpl((p) => ({ ...p, image_url: '', image_name: '' }))}
                        style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                      >
                        Убрать картинку
                      </button>
                    </div>
                  ) : null}
                </div>
                <label style={labelStyle}>Файл (CSV/XLS/XLSX/DOC/DOCX/PDF, опционально)</label>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx,.doc,.docx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      setSaving(true);
                      setError('');
                      try {
                        const doc = await uploadTemplateFile(f);
                        setNewTpl((p) => ({ ...p, ...doc }));
                        appendNewTemplateAttachment({
                          kind: 'document',
                          url: doc.file_url || '',
                          name: doc.file_name || f.name || '',
                          mime: doc.file_mime || '',
                        });
                      } catch (err) {
                        setError(err.message || 'Ошибка загрузки файла');
                      } finally {
                        setSaving(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {newTpl.file_url ? (
                    <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                        Файл: {newTpl.file_name || 'document'}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setNewTpl((p) => ({ ...p, file_url: '', file_name: '', file_mime: '' }))
                        }
                        style={{
                          border: 'none',
                          background: '#fee2e2',
                          color: '#b91c1c',
                          padding: '6px 10px',
                          borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      >
                        Убрать файл
                      </button>
                    </div>
                  ) : null}
                </div>
                {(newTpl.attachments || []).length > 0 ? (
                  <div style={{ marginBottom: 12, padding: 10, borderRadius: 10, border: '1px solid #bae6fd', background: '#f8fafc' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                      Вложения шаблона ({newTpl.attachments.length}) — можно отправлять несколько фото и файлов
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {newTpl.attachments.map((a, i) => (
                        <div key={`${a.url}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#334155' }}>
                          <span>{a.kind === 'image' ? 'Фото' : 'Документ'}: {a.name || a.url}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setNewTpl((p) => ({
                                ...p,
                                attachments: (p.attachments || []).filter((_, idx) => idx !== i),
                              }))
                            }
                            style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer' }}
                          >
                            убрать
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <label style={labelStyle}>Расписание отправки</label>
                <select
                  style={{ ...inputStyle, marginBottom: 12 }}
                  value={newTpl.schedule_recurrence}
                  onChange={(e) => setNewTpl((p) => ({ ...p, schedule_recurrence: e.target.value }))}
                >
                  <option value="none">Без расписания</option>
                  <option value="once">Один раз (дата и время)</option>
                  <option value="weekly">Раз в неделю (день недели)</option>
                  <option value="weekdays">По будням / ежедневно без выходных (пн–пт)</option>
                  <option value="weekends">Только по выходным (сб–вс)</option>
                </select>
                {newTpl.schedule_recurrence === 'once' ? (
                  <input
                    type="datetime-local"
                    style={{ ...inputStyle, marginBottom: 12 }}
                    value={newTpl.scheduled_local}
                    onChange={(e) => setNewTpl((p) => ({ ...p, scheduled_local: e.target.value }))}
                  />
                ) : null}
                {newTpl.schedule_recurrence === 'weekly' ? (
                  <>
                    <label style={labelStyle}>День недели</label>
                    <select
                      style={{ ...inputStyle, marginBottom: 12 }}
                      value={newTpl.schedule_weekday}
                      onChange={(e) => setNewTpl((p) => ({ ...p, schedule_weekday: Number(e.target.value) }))}
                    >
                      {WEEKDAY_OPTIONS.map((o) => (
                        <option key={o.v} value={o.v}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : null}
                {newTpl.schedule_recurrence === 'weekly' ||
                newTpl.schedule_recurrence === 'weekdays' ||
                newTpl.schedule_recurrence === 'weekends' ? (
                  <>
                    <label style={labelStyle}>Время отправки</label>
                    <input
                      type="time"
                      style={{ ...inputStyle, marginBottom: 12 }}
                      value={newTpl.schedule_time}
                      onChange={(e) => setNewTpl((p) => ({ ...p, schedule_time: e.target.value }))}
                    />
                  </>
                ) : null}
                <button
                  type="button"
                  disabled={saving}
                  onClick={createTemplate}
                  style={{
                    padding: '12px 20px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                  }}
                >
                  Добавить шаблон
                </button>
              </div>

              <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>Все шаблоны</h3>
              {templates.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: 14 }}>Пока нет шаблонов</p>
              ) : (
                templates.map((t) => (
                  <TemplateCard
                    key={t.id}
                    t={t}
                    saving={saving}
                    isMobile={isMobile}
                    isDeleting={deletingId === t.id}
                    swipeHandlers={swipeHandlersFor(t.id)}
                    setFormError={setError}
                    onToggleEnabled={(next) => toggleTemplateEnabled(t.id, next)}
                    onDelete={() => deleteTemplate(t.id)}
                    onSave={(body) => patchTemplate(t.id, body)}
                    onUploadImage={uploadTemplateImage}
                    onUploadFile={uploadTemplateFile}
                  />
                ))
              )}
            </div>
          ) : (
            <WorkQueueTab
              templates={templates}
              saving={saving}
              deletingId={deletingId}
              isMobile={isMobile}
              swipeHandlersFor={swipeHandlersFor}
              onToggleEnabled={toggleTemplateEnabled}
              onDelete={deleteTemplate}
              onOpenTemplates={() => setTab('templates')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ t, saving, onDelete, onSave, setFormError, onToggleEnabled, isMobile, isDeleting, swipeHandlers, onUploadImage, onUploadFile }) {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(t.title || '');
  const [body, setBody] = useState(t.body_text || '');
  const [imageUrl, setImageUrl] = useState(t.image_url || '');
  const [imageName, setImageName] = useState(t.image_name || '');
  const [fileUrl, setFileUrl] = useState(t.file_url || '');
  const [fileName, setFileName] = useState(t.file_name || '');
  const [fileMime, setFileMime] = useState(t.file_mime || '');
  const [attachments, setAttachments] = useState(normalizeTemplateAttachments(t));
  const [recurrence, setRecurrence] = useState(templateRecurrenceRaw(t));
  const [weekday, setWeekday] = useState(t.schedule_weekday != null ? Number(t.schedule_weekday) : 1);
  const [timeStr, setTimeStr] = useState(t.schedule_time || toTimeLocalValue(t.scheduled_at) || '09:00');
  const [localDt, setLocalDt] = useState(toDatetimeLocalValue(t.scheduled_at));

  useEffect(() => {
    setTitle(t.title || '');
    setBody(t.body_text || '');
    setImageUrl(t.image_url || '');
    setImageName(t.image_name || '');
    setFileUrl(t.file_url || '');
    setFileName(t.file_name || '');
    setFileMime(t.file_mime || '');
    setAttachments(normalizeTemplateAttachments(t));
    setRecurrence(templateRecurrenceRaw(t));
    setWeekday(t.schedule_weekday != null ? Number(t.schedule_weekday) : 1);
    setTimeStr(t.schedule_time || toTimeLocalValue(t.scheduled_at) || '09:00');
    setLocalDt(toDatetimeLocalValue(t.scheduled_at));
  }, [t.id, t.title, t.body_text, t.image_url, t.image_name, t.file_url, t.file_name, t.file_mime, t.attachments_json, t.scheduled_at, t.schedule_recurrence, t.schedule_weekday, t.schedule_time, t.enabled]);

  const rowSwipe = isMobile && !edit && swipeHandlers ? swipeHandlers : {};

  return (
    <div
      {...rowSwipe}
      style={{
        ...cardShell,
        padding: 16,
        opacity: isDeleting ? 0.55 : templateEnabled(t) ? 1 : 0.94,
        transition: 'opacity 0.15s ease',
        outline: isDeleting ? '2px dashed #f97316' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span>{t.title || 'Без названия'}</span>
            {!templateEnabled(t) ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.04,
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
                  color: '#475569',
                }}
              >
                Выкл
              </span>
            ) : (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.04,
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                  color: '#047857',
                }}
              >
                Вкл
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Бот: {t.bot_label || `#${t.bot_id}`}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            Chat ID:{' '}
            <span
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontWeight: 600,
                color: String(t.bot_chat_id || '').trim() ? '#0f172a' : '#dc2626',
              }}
            >
              {String(t.bot_chat_id || '').trim() || 'не задан'}
            </span>
          </div>
          {isMobile && !edit ? (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              Свайп влево или вправо — удалить
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexDirection: 'column' }}>
          <EnableSwitch
            on={templateEnabled(t)}
            disabled={saving || edit}
            onToggle={() => onToggleEnabled?.(!templateEnabled(t))}
            labelOn="Вкл"
            labelOff="Выкл"
          />
          <button
            type="button"
            aria-label="Удалить шаблон"
            disabled={saving || isDeleting}
            onClick={onDelete}
            title="Удалить"
            style={{
              border: 'none',
              background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
              color: '#b91c1c',
              padding: 10,
              borderRadius: 10,
              cursor: saving || isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
            }}
          >
            <FiTrash2 size={18} />
          </button>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 999,
              background:
                t.schedule_status === 'sent'
                  ? '#dcfce7'
                  : t.schedule_status === 'failed'
                    ? '#fee2e2'
                    : t.schedule_status === 'pending'
                      ? '#e0f2fe'
                      : '#f1f5f9',
              color:
                t.schedule_status === 'sent'
                  ? '#166534'
                  : t.schedule_status === 'failed'
                    ? '#b91c1c'
                    : t.schedule_status === 'pending'
                      ? '#0369a1'
                      : '#475569',
            }}
          >
            {statusRu(t.schedule_status)}
          </span>
        </div>
      </div>
      <pre
        style={{
          margin: '8px 0 12px',
          padding: 12,
          borderRadius: 10,
          background: 'linear-gradient(135deg, #f0f9ff 0%, #f5f3ff 100%)',
          border: '1px solid #e0e7ff',
          fontSize: 13,
          color: '#334155',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'inherit',
        }}
      >
        {t.body_text}
      </pre>
      {t.image_url ? (
        <div style={{ marginBottom: 10 }}>
          <img
            src={t.image_url}
            alt={t.image_name || 'template-image'}
            style={{ maxWidth: 240, width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, border: '1px solid #bae6fd' }}
          />
          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>{t.image_name || 'Изображение'}</div>
        </div>
      ) : null}
      {t.file_url ? (
        <div style={{ marginBottom: 10, fontSize: 12, color: '#475569', fontWeight: 600 }}>
          Файл: {t.file_name || 'document'}
        </div>
      ) : null}
      {attachments.length > 0 ? (
        <div style={{ marginBottom: 10, fontSize: 12, color: '#475569' }}>
          Вложений: {attachments.length}
        </div>
      ) : null}
      {formatScheduleSummary(t) ? (
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 8, fontWeight: 500 }}>{formatScheduleSummary(t)}</div>
      ) : null}
      {['weekly', 'weekdays', 'weekends'].includes(templateRecurrenceRaw(t)) && t.scheduled_at ? (
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
          Следующий запуск: {new Date(t.scheduled_at).toLocaleString()}
        </div>
      ) : null}
      {t.sent_at ? (
        <div style={{ fontSize: 13, color: '#16a34a', marginBottom: 8 }}>
          Отправлено: {new Date(t.sent_at).toLocaleString()}
        </div>
      ) : null}
      {t.last_error ? (
        <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 8 }}>{t.last_error}</div>
      ) : null}

      {!edit ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setEdit(true)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '2px solid #38bdf8',
              background: 'linear-gradient(180deg, #ffffff, #e0f2fe)',
              color: '#0369a1',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(14, 165, 233, 0.2)',
            }}
          >
            Изменить
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={labelStyle}>Название</label>
            <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Текст</label>
            <textarea style={{ ...inputStyle, minHeight: 88 }} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Картинка (PNG/JPG)</label>
            <ImagePicker
              isMobile={isMobile}
              disabled={saving}
              onPick={async (f) => {
                if (!f || !onUploadImage) return;
                try {
                  const up = await onUploadImage(f);
                  setImageUrl(up.image_url || '');
                  setImageName(up.image_name || '');
                  setAttachments((prev) => [...prev, { kind: 'image', url: up.image_url || '', name: up.image_name || f.name || '', mime: 'image/jpeg' }].slice(0, 12));
                } catch (err) {
                  setFormError?.(err.message || 'Ошибка загрузки картинки');
                }
              }}
            />
            {imageUrl ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <img
                  src={imageUrl}
                  alt={imageName || 'preview'}
                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #bae6fd' }}
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>{imageName || 'Изображение'}</span>
                <button
                  type="button"
                  onClick={() => {
                    setImageUrl('');
                    setImageName('');
                    setAttachments((prev) => prev.filter((x) => x.url !== imageUrl));
                  }}
                  style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                >
                  Удалить картинку
                </button>
              </div>
            ) : null}
          </div>
          <div>
            <label style={labelStyle}>Файл (CSV/XLS/XLSX/DOC/DOCX/PDF)</label>
            <input
              type="file"
              accept=".csv,.xls,.xlsx,.doc,.docx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={async (e) => {
                const f = e.target.files && e.target.files[0];
                if (!f || !onUploadFile) return;
                try {
                  const up = await onUploadFile(f);
                  setFileUrl(up.file_url || '');
                  setFileName(up.file_name || '');
                  setFileMime(up.file_mime || '');
                  setAttachments((prev) => [...prev, { kind: 'document', url: up.file_url || '', name: up.file_name || f.name || '', mime: up.file_mime || '' }].slice(0, 12));
                } catch (err) {
                  setFormError?.(err.message || 'Ошибка загрузки файла');
                } finally {
                  e.target.value = '';
                }
              }}
            />
            {fileUrl ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  {fileName || 'document'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setFileUrl('');
                    setFileName('');
                    setFileMime('');
                    setAttachments((prev) => prev.filter((x) => x.url !== fileUrl));
                  }}
                  style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
                >
                  Удалить файл
                </button>
              </div>
            ) : null}
            {attachments.length > 0 ? (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map((a, i) => (
                  <div key={`${a.url}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#334155' }}>
                    <span>{a.kind === 'image' ? 'Фото' : 'Документ'}: {a.name || a.url}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ border: 'none', background: 'transparent', color: '#b91c1c', cursor: 'pointer' }}
                    >
                      убрать
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <label style={labelStyle}>Расписание</label>
            <select
              style={{ ...inputStyle, marginBottom: 10 }}
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
            >
              <option value="none">Без расписания</option>
              <option value="once">Один раз (дата и время)</option>
              <option value="weekly">Раз в неделю (день недели)</option>
              <option value="weekdays">По будням / ежедневно без выходных (пн–пт)</option>
              <option value="weekends">Только по выходным (сб–вс)</option>
            </select>
            {recurrence === 'once' ? (
              <>
                <input type="datetime-local" style={inputStyle} value={localDt} onChange={(e) => setLocalDt(e.target.value)} />
                <button
                  type="button"
                  onClick={() => setLocalDt('')}
                  style={{ marginTop: 6, border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 13 }}
                >
                  Сбросить дату
                </button>
              </>
            ) : null}
            {recurrence === 'weekly' ? (
              <>
                <label style={{ ...labelStyle, marginTop: 10 }}>День недели</label>
                <select
                  style={{ ...inputStyle, marginBottom: 10 }}
                  value={weekday}
                  onChange={(e) => setWeekday(Number(e.target.value))}
                >
                  {WEEKDAY_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            {recurrence === 'weekly' || recurrence === 'weekdays' || recurrence === 'weekends' ? (
              <>
                <label style={labelStyle}>Время отправки</label>
                <input type="time" style={inputStyle} value={timeStr} onChange={(e) => setTimeStr(e.target.value)} />
              </>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                const origRec = templateRecurrenceRaw(t);
                const origWeekday = t.schedule_weekday != null ? Number(t.schedule_weekday) : 1;
                const origTime = String(t.schedule_time || toTimeLocalValue(t.scheduled_at) || '');
                const origLocal = toDatetimeLocalValue(t.scheduled_at);
                const scheduleChanged =
                  recurrence !== origRec ||
                  (recurrence === 'weekly' && Number(weekday) !== origWeekday) ||
                  (['weekly', 'weekdays', 'weekends'].includes(recurrence) && (timeStr || '') !== origTime) ||
                  (recurrence === 'once' && (localDt || '') !== (origLocal || '')) ||
                  (recurrence === 'none' && origRec !== 'none') ||
                  (recurrence !== 'none' && origRec === 'none');
                const payload = { title, body_text: body };
                payload.image_url = imageUrl || null;
                payload.image_name = imageName || null;
                payload.file_url = fileUrl || null;
                payload.file_name = fileName || null;
                payload.file_mime = fileMime || null;
                payload.attachments_json = JSON.stringify(attachments || []);
                if (scheduleChanged) {
                  if (recurrence === 'once' && !String(localDt || '').trim()) {
                    setFormError?.('Укажите дату и время для однократной отправки');
                    return;
                  }
                  if (
                    (recurrence === 'weekly' || recurrence === 'weekdays' || recurrence === 'weekends') &&
                    !String(timeStr || '').trim()
                  ) {
                    setFormError?.('Укажите время отправки');
                    return;
                  }
                  setFormError?.('');
                  payload.schedule_recurrence = recurrence;
                  payload.schedule_weekday = recurrence === 'weekly' ? Number(weekday) : null;
                  payload.schedule_time =
                    recurrence === 'weekly' || recurrence === 'weekdays' || recurrence === 'weekends' ? timeStr : null;
                  payload.scheduled_at = recurrence === 'once' ? (localDt ? fromDatetimeLocalToIso(localDt) : null) : null;
                }
                try {
                  await onSave(payload);
                  setEdit(false);
                } catch {
                  /* ошибка уже в модалке */
                }
              }}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                background: '#0ea5e9',
                color: '#fff',
                fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              Сохранить
            </button>
            <button
              type="button"
              onClick={() => {
                setEdit(false);
                setTitle(t.title || '');
                setBody(t.body_text || '');
                setImageUrl(t.image_url || '');
                setImageName(t.image_name || '');
                setFileUrl(t.file_url || '');
                setFileName(t.file_name || '');
                setFileMime(t.file_mime || '');
                setAttachments(normalizeTemplateAttachments(t));
                setRecurrence(templateRecurrenceRaw(t));
                setWeekday(t.schedule_weekday != null ? Number(t.schedule_weekday) : 1);
                setTimeStr(t.schedule_time || toTimeLocalValue(t.scheduled_at) || '09:00');
                setLocalDt(toDatetimeLocalValue(t.scheduled_at));
              }}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
